import React, { useEffect, useState, useCallback } from 'react';
import { Query, Mutation } from 'react-apollo';
import { withRouter } from 'react-router';
import Swal from 'sweetalert2';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { withStyles } from '@material-ui/core';
import { compose, get } from '../../utility';
import { withAppUser } from '../../shared/authorization/userConsumer';
import { ROLE_ID } from '../../constants';
import getAllSystemAclsQuery from '../api/getAllSystemAcls.query.graphql';
import getAllRolesQuery from '../api/getAllRoles.query.graphql';
import getAllUsersListQuery from '../api/getAllUsersList.query.graphql';
import updateUserMutation from '../api/updateUser.mutation.graphql';
import { UnControlledTable } from '../../shared/table';
import styles from './superAdmin.styles';

const SYSTEMS = [
  { id: 1, systemName: 'Control Panel' },
  { id: 2, systemName: 'Admin Panel' },
  { id: 3, systemName: 'Agent Panel' },
];

const ROLE_CATEGORIES = ['CP_ADMIN', 'ADMIN', 'AGENT'];

const refetchSystemAcls = { query: getAllSystemAclsQuery, variables: { first: 500 } };

/** react-table v6 sortMethod receives accessor values, not row objects. */
const compareDateValuesAsc = (a, b) => {
  const t1 = a ? new Date(a).getTime() : 0;
  const t2 = b ? new Date(b).getTime() : 0;
  return (Number.isNaN(t1) ? 0 : t1) - (Number.isNaN(t2) ? 0 : t2);
};

function isSuperAdmin(user) {
  if (!user) return false;
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

const makeColumns = (updateUser, handleRemove, handleOpenEditModal) => [
  {
    Header: 'User',
    id: 'user',
    sortable: true,
    accessor: (row) => {
      const u = row.userByUserId;
      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : row.userId;
    },
  },
  {
    Header: 'Email',
    id: 'email',
    sortable: true,
    accessor: (row) => get(row, 'userByUserId.email') || '—',
  },
  { Header: 'System', id: 'system', sortable: true, accessor: (row) => get(row, 'systemBySystemId.systemName') || row.systemId || '—' },
  { Header: 'Role', id: 'role', sortable: true, accessor: (row) => get(row, 'roleByRoleId.roleName') || row.roleId || '—' },
  { Header: 'Category', accessor: 'roleCategory', sortable: true },
  {
    Header: 'Created',
    id: 'createdAt',
    sortable: true,
    accessor: (row) => row.createdAt || '',
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : '—'),
  },
  {
    Header: 'Actions',
    id: 'actions',
    sortable: false,
    width: 200,
    Cell: ({ original }) => {
      const isProtected = Number(original.roleId) === ROLE_ID.CP_SEED_ADMIN;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={isProtected}
            onClick={() => !isProtected && handleOpenEditModal(original)}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            disabled={isProtected}
            onClick={() => !isProtected && handleRemove(original, updateUser)}
          >
            Remove
          </Button>
        </div>
      );
    },
  },
];

function filterUsersBySearch(users, search) {
  const q = (search || '').trim().toLowerCase();
  if (!q) return [];
  return users.filter((u) => {
    const hay = [u.email, u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });
}

function formatUserLabel(u) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || `User #${u.id}`;
}

function SystemAclsListC({ classes, user, history }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addSystemId, setAddSystemId] = useState(2);
  const [addRoleId, setAddRoleId] = useState('');
  const [addRoleCategory, setAddRoleCategory] = useState('ADMIN');
  const [addError, setAddError] = useState('');
  const [editingAclId, setEditingAclId] = useState(null);

  useEffect(() => {
    if (user && !isSuperAdmin(user)) history.replace('/admin');
  }, [user, history]);

  const resetModal = useCallback(() => {
    setUserSearch('');
    setSelectedUserId('');
    setAddSystemId(2);
    setAddRoleId('');
    setAddRoleCategory('ADMIN');
    setAddError('');
    setEditingAclId(null);
  }, []);

  const handleOpenModal = () => {
    resetModal();
    setModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    resetModal();
    setModalOpen(false);
  }, [resetModal]);

  const handleOpenEditModal = useCallback((acl) => {
    setEditingAclId(acl.id);
    setUserSearch('');
    setSelectedUserId(String(acl.userId));
    setAddSystemId(Number(acl.systemId));
    setAddRoleId(String(acl.roleId));
    setAddRoleCategory(acl.roleCategory);
    setAddError('');
    setModalOpen(true);
  }, []);

  const handleRemove = useCallback((acl, updateUser) => {
    Swal.fire({
      title: 'Confirm removal',
      text: 'This ACL will be removed from the user. Do you want to proceed?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (!result.isConfirmed) return;
      updateUser({
        variables: {
          id: acl.userId,
          user: { removeSystemAcls: [acl.id] },
        },
        refetchQueries: [refetchSystemAcls],
      })
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'ACL removed successfully',
            toast: true,
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .catch(() => {});
    });
  }, []);

  const handleAdd = useCallback((updateUser) => {
    const userId = selectedUserId ? Number(selectedUserId) : null;
    const roleId = addRoleId ? Number(addRoleId) : null;
    if (!userId || !roleId) {
      setAddError('Please select a user and a role.');
      return;
    }
    const isEditing = editingAclId != null;
    setAddError('');
    const userPayload = {
      systemAcls: [{
        systemId: addSystemId,
        roleId,
        roleCategory: addRoleCategory,
      }],
    };
    if (isEditing) {
      userPayload.removeSystemAcls = [Number(editingAclId)];
    }
    updateUser({
      variables: {
        id: userId,
        user: userPayload,
      },
      refetchQueries: [refetchSystemAcls],
    })
      .then(() => {
        setAddRoleId('');
        Swal.fire({
          icon: 'success',
          title: isEditing ? 'ACL updated successfully' : 'ACL added successfully',
          toast: true,
          showConfirmButton: false,
          timer: 2000,
        });
        if (isEditing) {
          handleCloseModal();
        }
      })
      .catch((e) => setAddError(e && e.message ? e.message : (isEditing ? 'Failed to update ACL' : 'Failed to add ACL')));
  }, [selectedUserId, addSystemId, addRoleId, addRoleCategory, editingAclId, handleCloseModal]);

  if (!user || !isSuperAdmin(user)) return null;

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="title">System ACLs (Super Admin)</Typography>

      <div className={classes.addAclToolbar}>
        <Button variant="contained" color="primary" onClick={handleOpenModal}>
          Add ACL
        </Button>
      </div>

      <Query query={getAllSystemAclsQuery} variables={{ first: 500 }}>
        {({ data: aclData, loading: aclLoading, error: aclError }) => {
          if (aclError) return <Typography color="error">Error loading system ACLs.</Typography>;
          const aclNodes = get(aclData, 'allSystemAcls.nodes', []);
          return (
            <Query query={getAllUsersListQuery} variables={{ first: 500 }}>
              {({ data: usersData }) => (
                <Query query={getAllRolesQuery} variables={{ first: 200 }}>
                  {({ data: rolesData }) => (
                    <Mutation mutation={updateUserMutation}>
                      {(updateUser) => {
                        const users = get(usersData, 'allUsers.nodes', []);
                        const roles = get(rolesData, 'allRoles.nodes', []);
                        const filteredUsers = filterUsersBySearch(users, userSearch);
                        const selectedUser = users.find(u => String(u.id) === String(selectedUserId));
                        const existingAclsForUser = aclNodes.filter(
                          a => String(a.userId) === String(selectedUserId),
                        );

                        return (
                          <React.Fragment>
                            <Dialog
                              open={modalOpen}
                              onClose={handleCloseModal}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogTitle>{editingAclId != null ? 'Edit system ACL' : 'Add system ACL'}</DialogTitle>
                              <DialogContent className={classes.aclDialogContent}>
                                {!editingAclId && (
                                <TextField
                                  className={classes.aclDialogSearch}
                                  label="Search users"
                                  placeholder="Type a name or email"
                                  value={userSearch}
                                  onChange={(e) => {
                                    setUserSearch(e.target.value);
                                    if (selectedUserId) setSelectedUserId('');
                                    setAddError('');
                                  }}
                                  fullWidth
                                  variant="outlined"
                                  margin="normal"
                                />
                                )}
                                {!editingAclId && !selectedUserId && userSearch.trim() && (
                                  <div className={classes.userPickList}>
                                    {filteredUsers.length === 0 ? (
                                      <div className={classes.userPickEmpty}>
                                        No users match your search.
                                      </div>
                                    ) : (
                                      filteredUsers.slice(0, 40).map(u => (
                                        <div
                                          key={u.id}
                                          role="button"
                                          tabIndex={0}
                                          className={classes.userPickItem}
                                          onClick={() => {
                                            setSelectedUserId(String(u.id));
                                            setUserSearch('');
                                            setAddError('');
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              setSelectedUserId(String(u.id));
                                              setUserSearch('');
                                              setAddError('');
                                            }
                                          }}
                                        >
                                          <div className={classes.userPickItemPrimary}>
                                            {formatUserLabel(u)}
                                          </div>
                                          {u.email && (
                                            <div className={classes.userPickItemSecondary}>
                                              {u.email}
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                                {!editingAclId && !selectedUserId && !userSearch.trim() && (
                                  <Typography variant="caption" color="textSecondary" component="p" className={classes.aclSearchHint}>
                                    Start typing to see matching users below.
                                  </Typography>
                                )}

                                {selectedUserId && selectedUser && (
                                  <div className={classes.selectedUserBanner}>
                                    <div className={classes.selectedUserBannerText}>
                                      <Typography className={classes.selectedUserName}>
                                        {formatUserLabel(selectedUser)}
                                      </Typography>
                                      {selectedUser.email && (
                                        <Typography className={classes.selectedUserEmail}>
                                          {selectedUser.email}
                                        </Typography>
                                      )}
                                    </div>
                                    {editingAclId == null && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setSelectedUserId('');
                                        setAddError('');
                                      }}
                                    >
                                      Change
                                    </Button>
                                    )}
                                  </div>
                                )}

                                {selectedUserId && selectedUser && (
                                  <div className={classes.aclDialogSection}>
                                    <Typography className={classes.aclDialogSectionTitle}>
                                      Existing ACLs for this user
                                    </Typography>
                                    {existingAclsForUser.length === 0 ? (
                                      <Typography variant="body2" color="textSecondary">
                                        No system ACLs yet.
                                      </Typography>
                                    ) : (
                                      <ul className={classes.existingAclList}>
                                        {existingAclsForUser.map(acl => (
                                          <li key={acl.id} className={classes.existingAclItem}>
                                            {get(acl, 'systemBySystemId.systemName') || acl.systemId}
                                            {' — '}
                                            {get(acl, 'roleByRoleId.roleName') || acl.roleId}
                                            {' '}
                                            ({acl.roleCategory})
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}

                                <Typography className={`${classes.aclDialogSectionTitle} ${classes.newAclSectionTitle}`}>
                                  {editingAclId != null ? 'ACL details' : 'New ACL'}
                                </Typography>
                                <div className={classes.aclDialogForm}>
                                  <TextField
                                    className={classes.aclOutlinedField}
                                    select
                                    fullWidth
                                    variant="outlined"
                                    label="System"
                                    value={addSystemId}
                                    onChange={(e) => setAddSystemId(Number(e.target.value))}
                                    margin="normal"
                                  >
                                    {SYSTEMS.map((s) => (
                                      <MenuItem key={s.id} value={s.id}>{s.systemName}</MenuItem>
                                    ))}
                                  </TextField>
                                  <TextField
                                    className={classes.aclOutlinedField}
                                    select
                                    fullWidth
                                    variant="outlined"
                                    label="Role"
                                    value={addRoleId}
                                    onChange={(e) => {
                                      setAddRoleId(e.target.value);
                                      setAddError('');
                                    }}
                                    margin="normal"
                                    SelectProps={{ displayEmpty: true }}
                                    InputLabelProps={{ shrink: true }}
                                  >
                                    <MenuItem value="" className={classes.roleSelectEmpty}>
                                      Select a role
                                    </MenuItem>
                                    {roles.map((r) => (
                                      <MenuItem key={r.id} value={String(r.id)}>
                                        {r.roleName} ({r.roleCategory})
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <TextField
                                    className={classes.aclOutlinedField}
                                    select
                                    fullWidth
                                    variant="outlined"
                                    label="Role category"
                                    value={addRoleCategory}
                                    onChange={(e) => setAddRoleCategory(e.target.value)}
                                    margin="normal"
                                  >
                                    {ROLE_CATEGORIES.map((c) => (
                                      <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                  </TextField>
                                </div>
                                {addError && (
                                  <Typography color="error" className={classes.addError}>{addError}</Typography>
                                )}
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={handleCloseModal} color="default">
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleAdd(updateUser)}
                                >
                                  {editingAclId != null ? 'Save changes' : 'Add ACL'}
                                </Button>
                              </DialogActions>
                            </Dialog>

                            {aclLoading ? <Typography color="textSecondary">Loading…</Typography>
                              : (
                                <UnControlledTable
                                  data={aclNodes}
                                    columns={makeColumns(updateUser, handleRemove, handleOpenEditModal)}
                                  defaultSorted={[{ id: 'user', desc: false }]}
                                />
                              )}
                          </React.Fragment>
                        );
                      }}
                    </Mutation>
                  )}
                </Query>
              )}
            </Query>
          );
        }}
      </Query>
    </div>
  );
}

export default compose(
  withStyles(styles),
  withAppUser,
  withRouter
)(SystemAclsListC);
